import { Patch } from 'immer';
import isEqual from 'lodash.isequal';
import { InfographState } from 'types/infographTypes';
import { groupIdCache } from 'widgets/sdk/GroupIdCache';
import { GroupWidgetData } from 'widgets/Widget.types';
import { HistoryRecord, PathSegmentName } from './history.types';
import { WidgetType } from 'types/widget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

/**
 * This can be more simplify once we implement group action
 *
 * Example:
 * r1 => {op: 'replace': path: ['widgets', '003.widget-1', ...]}
 * r2 => {op: 'replace': path: ['widgets', '003.widget-1', ...]}
 *
 * @param r1 - A history record
 * @param r2 - A compare history record
 * @returns  True if both records have similar op and path
 */
const hasSimilarPatch = (r1: HistoryRecord, r2: HistoryRecord) => {
  for (const [i, patch] of r1?.patches?.entries()) {
    const isSameOp = patch.op === r2?.patches?.[i]?.op;
    const isPathEqual = isEqual(patch.path, r2?.patches?.[i]?.path);

    if (isSameOp && isPathEqual) continue;
    return false;
  }
  return r1?.patches?.length === r2?.patches?.length;
};

/**
 * Return the patch type based on the patch's path array
 * If patch has the 'pages' as an first path segment, the patch type is pages
 * If patch has the 'widgets' as an first path segment, the patch type is pages
 * else the patch type is infographs
 * @param patch
 * @returns string
 */
const getPatchType = (patch: Patch): PathSegmentName => {
  if (patch.path[0] === 'pages') return 'pages';
  else if (patch.path[0] === 'widgets') return 'widgets';
  return 'infographs';
};

/**
 * Generate the path using the currnet applyPatch and infograph state
 * If patch has the 'pages' as an first path segment, the patch type is pages
 * If patch has the 'widgets' as an first path segment, the patch type is pages
 * else the patch type is infographs
 * @param state
 * @param patch
 * @returns
 */
const getPath = (state: InfographState, patch: Patch) => {
  const type = getPatchType(patch);
  const infographId = state.id;
  if (type === 'pages') {
    const pageId = patch.path[1].toString();
    return `infographs/${infographId}/pages/${pageId}`;
  } else if (type === 'widgets') {
    const widgetId = patch.path[1].toString();
    const pageId = getPageIdByWidgetId(state, widgetId);

    return `infographs/${infographId}/pages/${pageId}/widgets/${widgetId}`;
  }

  return `infographs/${infographId}`;
};

/**
 * Retrieve the pageId using 3 steps below
 * 1. Fetch the pageId from the widgetLayerOrder
 * 2. Fetch the pageId from the groupIdCache for group and responsive widget
 * 3. Fetch the pageId from the infographState using the memberWidgetIds for group and responsive widget
 *
 * Step 2, 3 seems the same but there is 2 reasons to use step 2 and 3
 * Step 2 has a better performance but some case it doens't have in the GroupIdCache(Adding new widget, etc)
 * So, it uses the groupIdCache first then it uses the step 3 to get the pageId
 *
 * @param state
 * @param widgetId
 * @returns {pageId}
 */
const getPageIdByWidgetId = (state: InfographState, widgetId: string) => {
  // Fetch the pageId from the widgetLayerOrder
  const pageId = getPageIdFromWidgetLayerOrder(state, widgetId);
  if (pageId) return pageId;

  /**
   * For responsive widget and group widget
   */
  // Fetch the pageId from the groupIdCache
  // Performance wise, it use this groupIdCache first.
  // However, If it couldn't find the pageId, it will use the getPageIdFromMemberWidgetIds function
  const pageIdFromGroupIdCache = getPageIdFromGroupIdCache(state, widgetId);
  if (pageIdFromGroupIdCache) return pageIdFromGroupIdCache;

  // Fetch the pageId from the infographState using the memberWidgetIds
  // This is for responsive widget and group widget
  const pageIdFromMemberWidget = getPageIdFromMemberWidgetIds(state, widgetId);
  if (pageIdFromMemberWidget) return pageIdFromMemberWidget;

  throw new Error(`Cannot find the pageId for widgetId: ${widgetId}`);
};

/**
 * Retrieve the pageId from the infographState data using the widgetLayerOrder
 * It doesn't return the pageId for responsive widget and group widget
 * If it doesn't have the pageId in the widgetLayerOrder, It search the parent id recursively.
 *
 * @param state
 * @param widgetId
 * @returns {pageId | null} It returns null if the widgetId is not found in the widgetLayerOrder
 */
const getPageIdFromWidgetLayerOrder = (state: InfographState, widgetId: string): string | null => {
  return Object.keys(state.pages).find((pageId) => state.pages[pageId].widgetLayerOrder.includes(widgetId)) || null;
};

/**
 * Retrieve the pageId for responsive widget and group widget from the groupIdCache
 *
 * @param state
 * @param widgetId
 * @returns {pageId | null} It returns null if the widgetId is not a member of any group widget
 */
const getPageIdFromGroupIdCache = (state: InfographState, widgetId: string) => {
  if (!groupIdCache.hasParentWidget(widgetId)) return null;

  const parentWidgetId = groupIdCache.getParentIdNested(widgetId);
  if (!parentWidgetId) return null;

  return getPageIdFromWidgetLayerOrder(state, parentWidgetId);
};

/**
 * Retrieve the pageId for responsive widget and group widget from the infographState
 * It parse the memberWidgetIds of the group widget to find the pageId
 *
 * @param state
 * @param widgetId
 * @returns {pageId | null} It returns null if the widgetId is not a member of any group widget
 */
const getPageIdFromMemberWidgetIds = (state: InfographState, widgetId: string): string | null => {
  const parentWidgetId = Object.keys(state.widgets)
    .filter((wId) => state.widgets[wId].hasOwnProperty('memberWidgetIds'))
    .filter((wId) => (state.widgets[wId] as GroupWidgetData).memberWidgetIds.includes(widgetId))
    // Filter after checking if it exists in the current widgetLayoutOrder for Group widget
    .find((wId) => (getWidgetTypeFromId(wId) === WidgetType.Group ? getPageIdFromWidgetLayerOrder(state, wId) : wId));

  if (!parentWidgetId) return null;

  // If the pageId is not found in the widgetLayerOrder, recursively find the parentWidgetId
  let pageId = getPageIdFromWidgetLayerOrder(state, parentWidgetId);
  if (!pageId) {
    pageId = getPageIdFromMemberWidgetIds(state, parentWidgetId);
  }
  return pageId;
};

export { hasSimilarPatch, getPath, getPageIdByWidgetId };
